import { z } from "zod";
import { SUPPORTED_LANGS } from "../i18n/langs";

export * from "./booking-time";
export * from "./booking-flow";
export * from "./club-table";
export * from "./full-name";
export * from "./guest-mix";
export * from "./lead-package";
export * from "./min-people";
export * from "./multi-pack";
export * from "./restaurant-seatings";

export const paymentMethodSchema = z.enum([
  "pay_on_arrival",
  "moroccan_card",
  "international_card",
]);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const checkoutRequestSchema = z.object({
  total: z.number().nonnegative(),
  adults: z.number().int().nonnegative(),
  children: z.number().int().nonnegative(),
  infants: z.number().int().nonnegative(),
  hour_count: z.number().int().nonnegative(),
  room_count: z.number().int().nonnegative(),
  group_count: z.number().int().nonnegative(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().email(),
  phone: z.string(),
  hotel: z.string().optional(),
  hours: z.string(),
  date: z.string(),
  package_id: z.number().int(),
  activity_id: z.number().int(),
  transport: z.boolean(),
  transport_price: z.number().nonnegative(),
  time_id: z.string(),
  payment_method: paymentMethodSchema,
  currency: z.string(),
  language: z.enum(["en", "fr", "ar"]),
});
export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

export const checkoutResponseSchema = z.object({
  reference: z.string(),
  message_app: z.string().optional(),
});
export type CheckoutResponse = z.infer<typeof checkoutResponseSchema>;

export const bookingStatusSchema = z.enum(["pending", "approved", "rejected"]);
export const bookingStatusResponseSchema = z.object({
  status: bookingStatusSchema,
  rejection_reason: z.string().nullable().optional(),
  updated_at: z.string(),
});
export type BookingStatusResponse = z.infer<typeof bookingStatusResponseSchema>;

export const bookingStatusListSchema = z.enum([
  "pending",
  "approved",
  "completed",
  "cancelled",
  "rejected",
]);
export type BookingListStatus = z.infer<typeof bookingStatusListSchema>;

export const modifiablePolicySchema = z.object({
  canChangePackage: z.boolean(),
  canChangeDate: z.boolean(),
  canChangeTime: z.boolean(),
  canChangeCounts: z.boolean(),
  canChangeTransport: z.boolean(),
  canCancel: z.boolean(),
});
export type ModifiablePolicy = z.infer<typeof modifiablePolicySchema>;

export const bookingListItemSchema = z.object({
  id: z.string(),
  reference: z.string(),
  activityId: z.string().nullable(),
  activityTitle: z.string(),
  activityImage: z.string().url().nullable(),
  cityName: z.string().nullable(),
  date: z.string(),
  time: z.string(),
  /** End of a flexible package's window ("15:00" for 13:00–15:00). Null = exact time. */
  timeEnd: z.string().nullable().optional(),
  status: bookingStatusListSchema,
  participantType: z.string(),
  participantCount: z.number().int().nonnegative(),
  totalPrice: z.number().nonnegative(),
  currency: z.string(),
  categoryName: z.string().nullable(),
  /**
   * A restaurant table, held rather than sold.
   *
   * Every surface that prints money needs to know: we bill nothing for a
   * table, so a total is either a lie or a zero that reads as a free meal. The
   * flag is computed once on the server (the category's template) instead of
   * each client guessing from a 0 price, which is also what a free activity
   * looks like.
   *
   * Optional, not defaulted: a default makes the field REQUIRED on the parsed
   * type, and every locally-built booking — drafts, tests, the bucket's
   * optimistic row — would have to carry a flag it cannot know.
   */
  tableBooking: z.boolean().optional(),
  /**
   * A desert camp, paid online in full from a secure link our team emails
   * after the reservation, not on arrival (`paysByLink`, computed on the
   * server). Optional for the same reason as `tableBooking`.
   */
  payByLink: z.boolean().optional(),
  /**
   * Where the activity is, for the map on the confirmation and the booking's
   * own page.
   *
   * On the list item rather than the detail because both surfaces want it: the
   * booking card offers "Directions" too, and it was dead — `bookingCoords`
   * read `latitude` off a payload that had never carried one, so the chip
   * never appeared and nobody noticed it was missing rather than hidden.
   *
   * `hideLocation` is the venue's own switch: some providers give an exact
   * address only after we confirm. Honour it; a map is not worth breaking that
   * promise.
   */
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  hideLocation: z.boolean().optional(),
  hasReview: z.boolean(),
  /**
   * Women-only trips: deposit sent to the organiser, rest on the day, and
   * whether the team has seen it arrive. `payment` (where to send it) is only
   * on the booking's own page, never on the list. Null on every other booking.
   */
  womenTrip: z
    .object({
      depositAmount: z.number(),
      restOnTheDay: z.number(),
      depositReceivedAt: z.string().nullable(),
      departure: z.string().nullable().optional(),
      payment: z
        .object({
          organiserName: z.string(),
          bankName: z.string().nullable(),
          bankAccountHolder: z.string().nullable(),
          bankRib: z.string().nullable(),
          cashplusName: z.string().nullable(),
          cashplusPhone: z.string().nullable(),
        })
        .nullable(),
    })
    .nullish(),
});
export type BookingListItem = z.infer<typeof bookingListItemSchema>;

export const bookingDetailSchema = bookingListItemSchema.extend({
  packageId: z.string().nullable(),
  packageName: z.string(),
  timeId: z.string(),
  /** End of a flexible package's window ("15:00" for 13:00–15:00). Null = exact time. */
  timeEnd: z.string().nullable().optional(),
  counts: z.object({
    adults: z.number().int(),
    children: z.number().int(),
    infants: z.number().int(),
    hours: z.number().int(),
    rooms: z.number().int(),
    groups: z.number().int(),
  }),
  transport: z.boolean(),
  transportPrice: z.number().nonnegative(),
  /**
   * Where to actually turn up, in the booking's own language.
   *
   * The activity's `pickupInfo` block, which is the only place a real meeting
   * point is written. Null when the venue has none — and then the client shows
   * NO meeting-point row at all. It used to fall back to the city name, so a
   * booking in Marrakech told the guest to meet at "Marrakech" (owner,
   * 2026-09-17). A city is not a meeting point; an empty row is honest and a
   * wrong one sends someone to the wrong place.
   *
   * Optional rather than defaulted: a default makes it REQUIRED on the parsed
   * type, and every locally-built booking would have to carry a field it
   * cannot know. Same reasoning as `tableBooking`.
   */
  meetingPoint: z.string().nullable().optional(),
  /**
   * A transfer's places, each labelled ("Your accommodation", "Restaurant",
   * ...). Set only for a transfer, which then has NO meeting point: its
   * pick-up is the guest's accommodation, and on an airport arrival the
   * driver takes the guest there (owner, 2026-09-24).
   */
  transferPlaces: z
    .array(
      z.object({
        kind: z.enum(["accommodation", "restaurant", "club", "camp"]),
        value: z.string(),
      }),
    )
    .nullable()
    .optional(),
  modifiable: modifiablePolicySchema,
});
export type BookingDetail = z.infer<typeof bookingDetailSchema>;

export const bookingLookupItemSchema = z.object({
  reference: z.string().min(1),
  email: z.string().email(),
});

export const bookingLookupRequestSchema = z.object({
  items: z.array(bookingLookupItemSchema).min(1).max(50),
});
export type BookingLookupRequest = z.infer<typeof bookingLookupRequestSchema>;

export const cancelBookingRequestSchema = z.object({
  reason: z.string().min(1),
  currency: z.string(),
  // All seven, like the API's langSchema; three sent es/it/de/nl English email.
  language: z.enum(SUPPORTED_LANGS),
});
export type CancelBookingRequest = z.infer<typeof cancelBookingRequestSchema>;

export const modifyBookingRequestSchema = z.object({
  package_id: z.number().int().optional(),
  date: z.string().optional(),
  time_id: z.string().optional(),
  counts: z
    .object({
      adults: z.number().int(),
      children: z.number().int(),
      infants: z.number().int(),
      hours: z.number().int(),
      rooms: z.number().int(),
      groups: z.number().int(),
    })
    .optional(),
  transport: z.boolean().optional(),
});
export type ModifyBookingRequest = z.infer<typeof modifyBookingRequestSchema>;

/**
 * Customer-facing short reference derived from the full `bookingId`.
 * Full bookingId format: `MA-<epoch-ms>-<32-char-hash>` (53 chars) — collision-safe
 * for the DB unique index, but unreadable in WhatsApp / email confirmations.
 * The short form keeps the `MA-<epoch-ms>` prefix (17 chars) — still recognizable
 * by ops via admin search (`ilike '%MA-<epoch>%'` matches the full row) and short
 * enough that customers can quote it back without copy-paste.
 *
 * Strings without the expected `MA-<epoch>-<hash>` shape are returned unchanged
 * (fallback for legacy IDs / synthesized MAJ-<uuid> placeholders).
 */
/**
 * Is this the short form a customer was shown, rather than the stored id?
 *
 * `MA-<epoch>` is on every customer surface; `MA-<epoch>-<hash>` is the column.
 * The API accepts both, and this is the guard that decides when it may treat
 * the input as a prefix — the shape is checked so `%` and `_` can never arrive
 * from a URL and widen a LIKE.
 */
export function isShortBookingReference(value: string | null | undefined): boolean {
  if (!value) return false;
  return /^[A-Za-z]{2,4}-\d{6,}$/.test(value);
}

export function shortBookingReference(
  value: string | null | undefined,
): string {
  if (!value) return "";
  const parts = value.split("-");
  if (parts.length >= 3) return `${parts[0]}-${parts[1]}`;
  return value;
}
export * from "./booking-rules";
